/**
 * Home guides for the kitchen, food and appliance chores (chores.ts), plus three topic guides: one-sink raw meat to
 * vegetables, vomit cleanup at home, and where food goes in the fridge. Every number and product cites a research row
 * (sources.ts). Maker instructions are "per [maker]"; where the sources are silent the guide says so.
 */
import { guide, s, WHERE, WHERE_CITES, NEVER, NEVER_CITES, type HomeGuide } from "./guide-kit.ts";
import { clean, dishes, food, hygiene, storage, thaw } from "./sources.ts";

const BLEACH_BOARDS = "For counters and boards (FSIS): 1 tablespoon of unscented liquid chlorine bleach in 1 gallon of water. Flood the surface, let it stand several minutes, then air-dry.";
const GLASS_NEVER = "Never use steel wool, abrasive powder cleansers, chlorine bleach, rust remover or ammonia on the glass (per Whirlpool).";
const AMMONIA_WARN = "Ammonia must never meet bleach: that makes toxic gas. Use one or the other, never both, and never in the same day's rinse water.";

export const FOOD_GUIDES: HomeGuide[] = [
  /* ───────── food safety ───────── */
  guide({
    id: "fridge-temp", title: "Check the fridge and freezer temperatures",
    when: ["Weekly is our suggested starting point: FDA says to use appliance thermometers but gives no checking interval.", "After a power outage, before deciding what to keep."],
    need: ["An appliance thermometer in the fridge and one in the freezer."],
    steps: [
      "Keep one appliance thermometer in the fridge and one in the freezer, at the front where they're easy to read.",
      "Read the fridge thermometer: it should be 40°F or below.",
      "Read the freezer thermometer: it should be 0°F or below.",
      "Too warm? Turn the control colder and check again later; don't overpack, cold air has to move around the food.",
      "After a power outage: fridge food should be safe if the power was out no more than 4 hours.",
      "Freezer food that still has ice crystals, or is 40°F or below, may be refrozen or cooked.",
    ],
    where: ["Thermometers stay in the fridge and freezer, at the front.", WHERE.food],
    never: ["Never judge the temperature by feel: appliance thermometers are the best way to know (FDA).", "Never overpack the fridge: cold air must circulate."],
    easy: { title: "Check the fridge", why: "Germs grow fast when the fridge gets warm.", steps: [
      s("thermo", "Read the thermometer at the front of the fridge."),
      s("fridge", "Check the fridge.", { check: { ask: "Is it 40°F or colder?", ifNo: "Turn the fridge colder and check again later." } }),
      s("fridge", "Check the freezer.", { check: { ask: "Is it 0°F or colder?", ifNo: "Turn the freezer colder and check again later." } }),
      s("box", "Leave space so cold air moves around the food."),
      s("write", "Write down what you saw."),
    ] },
    // 5-minute version: the first essential Easy steps, nothing new (guide-kit quick).
    quick: { steps: [0, 1, 2] },
    citations: [...food("H2", "H3", "H8"), ...storage("H25")], keywords: ["fridge", "freezer", "refrigerator", "temperature", "thermometer", "40", "power outage"],
  }),
  guide({
    id: "use-by-check", title: "Throw out food past its time",
    when: ["Once a week (USDA FSIS).", "Before a big grocery trip, so new food goes behind old."],
    need: [{ say: "A trash bag.", buy: "trash_bags" }, { say: "A marker for dating what stays.", buy: "date_marker" }],
    steps: [
      "Go shelf by shelf through the fridge.",
      "Throw out perishables past their time: cooked leftovers after 3–4 days (FDA chart; FSIS says 4 days, use the shorter), raw poultry and ground meat after 1–2 days.",
      "Check the use-by list in the app for anything with a date.",
      "Move older food to the front, newer behind it.",
      "Wipe up spills with hot soapy water, then rinse.",
    ],
    where: [WHERE.food],
    never: ["Never taste or smell food to decide if it's safe.", "Never keep leftovers past 3–4 days in the fridge; freeze them sooner if you won't eat them."],
    easy: { title: "Clean out old food", why: "Old food can make people sick even if it looks fine.", steps: [
      s("fridge", "Open the fridge and look at each shelf."),
      s("trash", "Throw out leftovers older than 3 days."),
      s("trash", "Throw out raw chicken or ground meat older than 1 day."),
      s("check", "Check dates in the app."),
      s("food", "Move older food to the front."),
      s("soap", "Wipe spills with hot soapy water."),
    ] },
    // 5-minute version: the first essential Easy steps, nothing new (guide-kit quick).
    quick: { steps: [0, 1, 2] },
    citations: [...storage("H26", "H55"), ...food("H5"), ...thaw("T9"), ...clean("K6")], keywords: ["use by", "expired", "leftovers", "old food", "fridge clean out", "how long"],
  }),
  guide({
    id: "counters", title: "Wash and sanitize the kitchen counters",
    when: ["After preparing each food item (FDA).", "Always after raw meat, poultry, seafood or eggs."],
    need: [{ say: "Hot soapy water and a clean cloth or paper towels.", buy: ["dish_soap", "wiping_cloths", "paper_towels"] }, "For counters and boards: unscented liquid chlorine bleach and a gallon of water (FSIS mix)."],
    steps: [
      "Clear the counter and wipe off crumbs and food.",
      "Wash with hot soapy water.",
      "After raw meat, poultry or seafood, sanitize: " + BLEACH_BOARDS,
      "Let it air-dry.",
      "Consider paper towels for kitchen surfaces; cloth towels go in the hot wash often.",
    ],
    where: [WHERE.cleaners, WHERE.bleachMix, WHERE.cloths],
    never: [NEVER.mixBleach, NEVER.foodContainer, "Never use the vomit-cleanup bleach strength or the general disinfecting mix as your counter mix: each strength has its own job."],
    easy: { title: "Clean the kitchen counter", why: "Raw meat juice can spread germs to food you don't cook.", steps: [
      s("box", "Take everything off the counter."),
      s("trash", "Wipe off crumbs and food."),
      s("soap", "Wash it with hot soapy water."),
      s("chemical", "Mix 1 tablespoon bleach in 1 gallon of water."),
      s("spray", "Wet the counter with the bleach mix."),
      s("wait", "Leave it wet for several minutes.", { waitSeconds: 180 }),
      s("check", "Let it dry by itself."),
    ] },
    // 5-minute version: the first essential Easy steps, nothing new (guide-kit quick).
    quick: { steps: [0, 1, 2, 6], fullIf: "Always after raw meat, poultry, seafood or eggs." },
    citations: [...food("H20", "H24", "H27", "H33"), ...WHERE_CITES.cleaners, ...WHERE_CITES.bleachMix, ...NEVER_CITES.mixBleach],
    keywords: ["counter", "countertop", "sanitize", "kitchen counter", "bleach", "raw meat"],
  }),
  guide({
    id: "dish-towels", title: "Change the kitchen towels",
    when: ["Daily is the common recommendation (University of Nebraska Extension). Cleveland Clinic says at least weekly; the stricter food guidance wins.", "Right away after a towel touches raw meat, poultry or seafood juices."],
    need: ["A stack of clean kitchen towels.", { say: "Laundry detergent.", buy: "laundry_detergent" }],
    steps: [
      "Put out a clean towel each day.",
      "A towel used on raw meat, poultry or seafood goes straight into the laundry.",
      "Wash kitchen towels on the hot cycle.",
      "Dry them completely on a high setting.",
    ],
    where: [WHERE.towels, WHERE.laundry],
    never: ["Never rub or pat clean dishes dry with a dish towel: let them air-dry (CDC, NDSU).", "Never reuse a towel that wiped raw meat juice."],
    easy: { title: "Change the kitchen towel", why: "Damp towels hold germs from food and hands.", steps: [
      s("towel", "Put out a clean kitchen towel today."),
      s("trash", "Put the used towel in the laundry."),
      s("stop", "Change it now if it touched raw meat juice."),
      s("water", "Wash kitchen towels on the hot cycle."),
      s("check", "Dry them all the way on high heat."),
    ] },
    // 5-minute version: the first essential Easy steps, nothing new (guide-kit quick).
    quick: { steps: [0, 1, 2] },
    citations: [...food("H33", "H34", "H35"), ...clean("K8"), ...WHERE_CITES.towels], keywords: ["dish towel", "kitchen towel", "towels", "dishcloth"],
  }),
  guide({
    id: "sponge-sanitize", title: "Clean the kitchen sponge",
    when: ["At least once a week (Clemson Extension)."],
    need: ["A microwave (sponge with no metal in it), or a dishwasher with a heated drying cycle, or a new sponge."],
    steps: [
      "Pick one way: microwave, dishwasher or replace it.",
      "Microwave: wet the sponge. Only a sponge with no metal pieces. Microwave it on high for 1 minute (USDA ARS: killed 99.99999% of bacteria).",
      "Let it cool before you touch it.",
      "Dishwasher: run it through a full cycle with heated drying (USDA ARS: killed 99.9998%).",
      "Replace it when it's torn or discolored, and at least every other week.",
    ],
    where: [WHERE.cloths],
    never: ["Never microwave a dry sponge or one with metal in it (Clemson says wet, no metal).", "Don't rely on a bleach, lemon or water soak: in the USDA ARS study they killed only 37–87% of bacteria.", "Never call a cleaned sponge germ-free: sanitized sponges held no fewer bacteria in one study."],
    easy: { title: "Clean the sponge", why: "A sponge holds germs from every dish it touches.", steps: [
      s("stop", "Never microwave a dry sponge or one with metal.", { why: "Clemson says to microwave it wet, with no metal in it.", cite: food("H31") }),
      s("stop", "Don't call a cleaned sponge germ-free.", { why: "Cleaned sponges held as many germs as unclean ones in a study.", cite: food("H32") }),
      s("clean", "Pick how to clean the sponge.", { why: "A microwave and a dishwasher both worked in a USDA study.", cite: food("H29"), choose: { ask: "How do you want to clean the sponge?", options: [{ key: "microwave", label: "Microwave it", requires: "microwave" }, { key: "dishwasher", label: "Dishwasher", requires: "dishwasher" }, { key: "replace", label: "Replace it" }] } }),
      s("check", "Check that the sponge has no metal in it.", { only: "microwave", why: "Metal can spark in a microwave.", cite: food("H31") }),
      s("water", "Wet the sponge all the way.", { only: "microwave", why: "A wet sponge heats up and kills germs.", cite: food("H31") }),
      s("power", "Heat it in the microwave on high for 1 minute.", { only: "microwave", waitSeconds: 60, why: "One minute killed 99.99999% of bacteria in a USDA study.", cite: food("H29") }),
      s("wait", "Let it cool before you touch it.", { only: "microwave", waitSeconds: 300, why: "It comes out very hot.", cite: "advice" }),
      s("power", "Run it through a full cycle with heated dry.", { only: "dishwasher", why: "A dishwasher cycle with drying killed 99.9998% in a USDA study.", cite: food("H29") }),
      s("trash", "Throw away the old sponge.", { only: "replace", why: "Cleaning does not make an old sponge safe.", cite: food("H32") }),
      s("clean", "Put out a new sponge.", { only: "replace", why: "Clemson says to replace a worn sponge.", cite: food("H31") }),
      s("write", "Write down the day you changed it.", { only: "replace", why: "So you know when to change it again.", cite: "advice" }),
    ] },
    citations: food("H29", "H30", "H31", "H32"), keywords: ["sponge", "microwave sponge", "clean sponge", "dishwasher"],
  }),
  guide({
    id: "sponge-replace", title: "Replace the kitchen sponge",
    when: ["At least every other week (Cleveland Clinic). A 2017 study suggests weekly.", "Sooner when it's torn or discolored (Clemson)."],
    need: ["A new sponge."],
    steps: ["Throw the old sponge away.", "Put out the new one.", "Keep cleaning it weekly in the microwave or dishwasher."],
    where: [WHERE.cloths],
    never: ["Never keep a sponge past two weeks just because it was cleaned: used sponges that were sanitized held no fewer bacteria in a 2017 study."],
    easy: { title: "Get a new sponge", why: "Old sponges hold germs even after cleaning.", steps: [
      s("trash", "Throw away the old sponge."),
      s("clean", "Put out a new sponge."),
      s("write", "Write down the day you changed it."),
    ] },
    citations: [...clean("K8"), ...food("H31", "H32")], keywords: ["sponge", "replace sponge", "new sponge"],
  }),
  guide({
    id: "trash-out", title: "Take out the kitchen trash",
    when: ["Regularly (Clemson Extension). No source gives a number; every two days is our suggested starting point."],
    need: [{ say: "Trash can liners.", buy: "trash_bags" }],
    steps: ["Tie the bag and take it outside.", "Put in a new liner.", "Close the lid.", "Wash your hands."],
    where: ["Liners next to the can; the can has a closing lid."],
    never: ["Never leave food trash in an open can: use a can with a lid."],
    easy: { title: "Take out the trash", why: "Food trash draws bugs and mice.", steps: [
      s("trash", "Tie the trash bag shut."),
      s("trash", "Take it outside."),
      s("box", "Put a new bag in the can."),
      s("check", "Close the lid."),
      s("hands", "Wash your hands."),
    ] },
    // 5-minute version: the first essential Easy steps, nothing new (guide-kit quick).
    quick: { steps: [0, 1, 2, 3, 4] },
    citations: [...food("H48"), ...hygiene("C1")], suggested: "Clemson says empty it regularly but gives no interval.", keywords: ["trash", "garbage", "take out trash"],
  }),
  guide({
    id: "trash-can-clean", title: "Clean the kitchen trash can",
    when: ["No source gives an interval (sources say \"regularly\" and \"sanitize bin\"). Monthly is our suggested starting point."],
    need: [{ say: "Gloves.", buy: "cleaning_gloves" }, "Hot soapy water and a scrub brush.", "Unscented liquid chlorine bleach for the rinse (Clemson).", "Disinfecting wipes for the lid and handle."],
    steps: [
      "Take out the bag.",
      "Scrub the can inside and out with hot soapy water.",
      "For the rinse (Clemson): add 1/4 cup of chlorine bleach to the rinse water. This is Clemson's trash-can rinse, not a counter mix.",
      "Let it drain and dry.",
      "Wipe the lid and handle with a disinfecting wipe; keep it visibly wet 4 minutes (per Clorox).",
      "Put in a new liner.",
    ],
    where: [WHERE.cleaners],
    never: [NEVER.mixBleach, "Never use the trash-can rinse on food counters."],
    easy: { title: "Clean the trash can", why: "A dirty can smells and draws pests.", steps: [
      s("gloves", "Put on gloves."),
      s("trash", "Take out the bag."),
      s("soap", "Scrub the can with hot soapy water."),
      s("chemical", "Add 1/4 cup bleach to the rinse water."),
      s("water", "Rinse the can and let it drain."),
      s("spray", "Wipe the lid with a disinfecting wipe.", { waitSeconds: 240 }),
      s("check", "Let it dry, then put in a new bag."),
    ] },
    citations: [...food("H48", "H49"), ...clean("K1", "K3"), ...NEVER_CITES.mixBleach],
    products: [{ job: "Lid and handle disinfecting", examples: ["Clorox Disinfecting Wipes"], citations: clean("K3") }],
    suggested: "No authoritative home interval for cleaning the trash can.", keywords: ["trash can", "garbage can", "bin"],
  }),
  guide({
    id: "pantry-check", title: "Check the pantry and use older cans first",
    when: ["Every few weeks (USDA FSIS). We read that as about every 3 weeks."],
    need: [{ say: "A marker.", buy: "date_marker" }],
    steps: [
      "Look at each can and package.",
      "Throw away swollen cans, deeply dented cans (a dent you can lay a finger into), cans with a deep dent on a seam, and heavily rusted cans. Surface rust that rubs off is not serious.",
      "Write the purchase month and year on new can lids.",
      "Put the oldest in front and new stock behind (FIFO).",
      "Keep cans cool, dry and off the floor; below 85°F is best.",
    ],
    where: [WHERE.food, "Never above or beside the stove, under the sink, or in a damp garage or basement (FSIS)."],
    never: ["Never store cans above or beside the stove or under the sink.", "Never keep cans on bare concrete."],
    easy: { title: "Check the pantry", why: "Old or damaged cans can make people sick.", steps: [
      s("check", "Look at every can."),
      s("trash", "Throw away cans that bulge."),
      s("trash", "Throw away cans with deep dents or heavy rust."),
      s("write", "Write the month and year on new cans."),
      s("box", "Put the oldest cans in front."),
      s("box", "Keep cans off the floor."),
    ] },
    citations: storage("H2", "H5", "H6", "H7", "H55"), keywords: ["pantry", "cans", "canned food", "fifo", "dented can"],
  }),
  guide({
    id: "fridge-clean", title: "Clean out and wipe the fridge",
    when: ["Monthly (American Cleaning Institute: clean kitchen appliances).", "Once a week, throw out food past its time (USDA FSIS).", "Right away after raw meat juice, a spill or spoiled food (CDC)."],
    need: [{ say: "A trash bag.", buy: "trash_bags" }, { say: "Hot soapy water, or baking soda and water.", buy: ["dish_soap", "baking_soda"] }, { say: "A clean cloth.", buy: "wiping_cloths" }, { say: "After raw juice, a spill or spoiled food: liquid bleach and a gallon of water (CDC), and gloves.", buy: ["bleach", "gloves"] }],
    steps: [
      "Go shelf by shelf; throw out perishables past their time (FSIS).",
      "Ask: any raw meat juice, a spill or spoiled food? If yes, throw out food it touched. Don't leave food out of the fridge more than 2 hours (CDC).",
      "No juice or spill: take food out shelf by shelf. Wipe shelves and drawers with hot soapy water, or a baking soda and water mix, then rinse.",
      "Use a baking-soda paste on stains; keep an open box of baking soda for odors.",
      "Juice, spill or spoiled food: take out the shelves and drawers that come out. Wash them by hand in hot soapy water (CDC).",
      "Wipe the inside with hot soapy water, then clean water to rinse off the soap. Wipe inside the doors too (CDC).",
      "Sanitize with a solution of 1 tablespoon of liquid bleach in 1 gallon of water (CDC). CDC gives no wait time and does not say scented or unscented.",
      "Let the parts air dry or dry them with a clean towel (CDC).",
      "Put food back: raw meat sealed in a container on the bottom shelf (USDA FSIS, CDC).",
    ],
    where: [WHERE.food],
    never: ["Never use solvent cleaners or abrasives inside the fridge: fumes and tastes can reach food (USDA FSIS).", "Never mix bleach with ammonia, vinegar or other cleaners (CDC)."],
    easy: { title: "Clean out the fridge", why: "Old food and spills spread germs to other food.", steps: [
      s("trash", "Throw away old or spoiled food from every shelf.", { why: "Old food can make people sick even if it looks fine.", cite: [...storage("H26"), ...food("H5")] }),
      s("fridge", "Look for raw meat juice, a spill or spoiled food.", { why: "Juice and spills carry germs onto other food.", cite: [...food("H50"), ...storage("H23")], choose: { ask: "Any raw meat juice, a spill or spoiled food in there?", options: [{ key: "yes", label: "Yes, I see some" }, { key: "no", label: "No, it looks clean" }] } }),
      s("stop", "Never mix bleach with ammonia or other cleaners.", { only: "yes", why: "The mix makes a toxic gas.", cite: [...NEVER_CITES.mixBleach] }),
      s("trash", "Throw away food the juice or spill touched.", { only: "yes", needs: ["gloves", "trash_bags"], why: "Germs can spread to food that touched it. Keep the rest out under 2 hours.", cite: food("H50", "H51") }),
      s("box", "Take out the shelves and drawers that come out.", { only: "yes", why: "You can only clean under and behind them with them out.", cite: food("H50") }),
      s("soap", "Wash them and the inside with hot soapy water.", { only: "yes", needs: ["dish_soap", "wiping_cloths"], why: "Soap lifts the dirt so sanitizer can reach the surface.", cite: food("H50") }),
      s("water", "Wipe it all with clean water to rinse.", { only: "yes", why: "Soap left behind can block the sanitizer.", cite: food("H50") }),
      s("spray", "Sanitize with 1 tablespoon of bleach in 1 gallon of water.", { only: "yes", needs: ["bleach"], why: "Bleach kills the germs soap leaves behind.", cite: food("H50") }),
      s("wait", "Let it all air dry.", { only: "yes", why: "Put the parts back dry.", cite: "advice" }),
      s("box", "Take the food off one shelf.", { only: "no", why: "A clear shelf is easy to wipe.", cite: "advice" }),
      s("soap", "Wipe the shelf with hot soapy water.", { only: "no", why: "Hot soapy water lifts spills.", cite: clean("B2") }),
      s("water", "Wipe it again with clean water.", { only: "no", why: "Rinsing takes the soap off.", cite: clean("B2") }),
      s("towel", "Dry the shelf.", { only: "no", why: "A dry shelf keeps food from sitting in water.", cite: "advice" }),
      s("food", "Put raw meat sealed in a container on the bottom shelf.", { why: "Raw juices can't drip onto food below.", cite: [...storage("H23"), ...storage("H24")] }),
    ] },
    citations: [...clean("B2", "K2", "K6"), ...storage("H23", "H24", "H26"), ...food("H5", "H50", "H51"), ...NEVER_CITES.mixBleach], keywords: ["fridge", "refrigerator", "clean fridge", "shelves", "raw meat juice", "fridge spill", "spoiled food in fridge", "old food"],
  }),

  guide({
    id: "microwave", title: "Clean the microwave",
    when: ["Monthly (American Cleaning Institute: clean kitchen appliances)."],
    need: [{ say: "Mild dish liquid and a damp cloth, or baking soda and water.", buy: ["dish_soap", "wiping_cloths", "baking_soda"] }, { say: "A microwave-safe cup of water.", noBuy: "a cup from your cupboard" }, "A plastic scrub pad."],
    steps: [
      "Unplug the microwave first (GE).",
      "For stuck soil, steam it: heat 1 cup of water on full power 3 minutes, then let it stand 5 minutes (GE).",
      "Plug it back in only for steaming; unplug again to wipe.",
      "Wipe the inside with mild dish liquid on a damp cloth, or baking soda and water.",
      "Use a plastic pad on stuck spots.",
      "Wipe the outside.",
    ],
    where: [WHERE.cleaners],
    never: ["Never use oven cleaners, abrasive cleaners, ammonia or bleach inside: they can make the plastic brittle (GE)."],
    easy: { title: "Clean the microwave", why: "Food splatter burns on and gets harder to clean.", steps: [
      s("food", "Put 1 cup of water in the microwave."),
      s("power", "Heat it on full power for 3 minutes.", { waitSeconds: 180 }),
      s("wait", "Leave the door shut for 5 minutes.", { waitSeconds: 300 }),
      s("power", "Unplug the microwave."),
      s("soap", "Wipe the inside with mild dish soap."),
      s("clean", "Use a plastic pad on stuck spots."),
      s("stop", "Never use bleach, ammonia or oven cleaner inside."),
    ] },
    citations: clean("B2", "A14", "A15"), keywords: ["microwave", "clean microwave", "steam"],
  }),
  guide({
    id: "dishwasher-filter", title: "Rinse the dishwasher filter",
    when: ["About once a month, more with frequent use (per Whirlpool). Check your model's manual."],
    need: [{ say: "Running water.", noBuy: "tap water" }, { say: "A soft brush (no wire brush).", buy: "scrub_brush" }],
    steps: [
      "Take out the bottom rack.",
      "Twist out the upper filter and pull out the lower one (Whirlpool models).",
      "Rinse both under running water.",
      "Put them back and lock the upper filter in place.",
    ],
    where: ["The filter lives in the dishwasher; nothing extra to store."],
    never: ["Never use a wire brush or scouring pad on the filters (Whirlpool)."],
    easy: { title: "Rinse the dishwasher filter", why: "A clogged filter leaves food on clean dishes.", steps: [
      s("box", "Pull out the bottom rack."),
      s("wrench", "Turn the top filter to take it out."),
      s("wrench", "Pull out the bottom filter."),
      s("water", "Rinse both under running water."),
      s("stop", "Never scrub them with a wire brush."),
      s("wrench", "Put them back and turn to lock."),
    ] },
    citations: clean("K7"), keywords: ["dishwasher", "dishwasher filter", "filter"],
  }),
  /* ───────── dishes: dishwasher loading, cast iron, drying mats (research/dishwasher-and-dishes.md) ───────── */
  guide({
    id: "load-dishwasher", title: "Load the dishwasher", kind: "topic",
    when: [
      "Every time you run the dishwasher.",
      "Rack layouts differ by model: every maker read (Whirlpool, GE, Bosch, Samsung) says your owner's manual comes first.",
    ],
    need: [
      "Automatic dishwasher detergent, never hand dish soap (Whirlpool, GE).",
      { say: "Liquid rinse aid in the rinse aid dispenser (Whirlpool, GE, Samsung).", buy: "rinse_aid" },
      { say: "The trash can, for scraping plates (Bosch, GE; detergent makers Cascade and Finish).", noBuy: "the trash can you already have" },
    ],
    steps: [
      "Scrape food, bones, seeds, toothpicks and paper into the trash. Don't pre-rinse (Whirlpool, Bosch, Samsung; detergent makers Cascade and Finish agree). If you won't run it soon, scrape or rinse so food doesn't dry on (GE).",
      "Load only items labeled dishwasher-safe; hand-wash the rest (Whirlpool).",
      "Bottom rack: plates slotted in on their side; pots, pans, platters and large bowls at the back, sides or corners, angled down (Whirlpool, GE).",
      "Face the dirty sides down and toward the center and spray arms; put open items like pots and cups face down (Whirlpool, Samsung). Newer GE models don't need dishes to face the center; follow your manual (GE).",
      "Keep large items from blocking the spray arms, the wash tower opening in the center of the bottom rack, and the detergent dispenser (Whirlpool, GE).",
      "Top rack: glasses, cups, mugs, saucers and small bowls, face down, at an angle and spaced apart (Whirlpool, GE, Samsung, Bosch). Cups and glasses fit best along the sides; wine glasses only if they fit without hitting the roof (GE).",
      "Plastics go on the top rack. Whirlpool says never the bottom rack, some Samsung models say the same, and Bosch allows anywhere; the top rack suits all three. Don't wedge plastics in or rest heavy items on them (Whirlpool).",
      "Silverware basket: knives point down (Whirlpool, Bosch, Samsung). Don't let spoons and forks nest (Whirlpool, Samsung, Bosch). Whirlpool says to mix the types; GE says covered baskets space them for you.",
      "Serving spoons and odd-shaped utensils: the top rack, or the third rack if you have one (Whirlpool, GE).",
      "Cutting boards: plastic, acrylic and glass boards can go in (FSIS). Keep every wood board out: its maker says it is not dishwasher safe (John Boos), and laminated boards may crack (FSIS). Wash a wood board by hand, don't soak it, and oil it when it looks dry (John Boos).",
      "Keep silver apart from stainless steel: silver touching stainless can tarnish (Whirlpool).",
      "Don't overload: leave space so water and detergent reach every surface (Whirlpool, Samsung).",
      "Turn the spray arm by hand before you start. If it hits something, move the dishes (Whirlpool, GE, Samsung).",
      "Add automatic dishwasher detergent just before you start; packs go in the main compartment with the lid closed (Whirlpool, GE).",
      "Keep the rinse aid dispenser filled to the max line without overfilling (Bosch). A full Whirlpool dispenser lasts about a month (Whirlpool).",
      "Run the kitchen hot tap until the water is hot, turn it off, then start the dishwasher (Whirlpool, GE). Water should arrive at 120°F or hotter (Whirlpool); GE says 120–150°F.",
      "Use heated dry for the driest dishes; without it, open the door when the cycle ends (Whirlpool). Hot water with heated dry or a sanitize setting helps kill more germs (CDC).",
      "Put small items in a closed-top basket or mesh bag so they don't reach the filter (CDC).",
      "Unload soon: items left sitting in the dishwasher can rust or pit (Whirlpool).",
      "Rinse the filter about once a month (Whirlpool, GE). See \"Rinse the dishwasher filter\".",
    ],
    where: ["Dishwasher detergent: tightly closed, in a cool, dry place (Whirlpool, GE); latched or up high where children live or visit (Poison Control)."],
    never: [
      "Never use hand dish soap in the dishwasher: its suds can push water out (Whirlpool, GE).",
      "Never put in cast iron (Lodge, Whirlpool), copper or wooden utensils (Whirlpool), or anything not labeled dishwasher-safe (Whirlpool).",
      "Never put gold- or silver-trimmed or hand-painted china in the dishwasher (per Samsung). Whirlpool says rims wear mostly from handling; follow your china maker.",
      "Never block the spray arms or the detergent dispenser (Whirlpool, GE).",
    ],
    easy: { title: "Load the dishwasher", why: "Dishes come out clean when water can reach them.", steps: [
      s("trash", "Scrape food into the trash. Don't rinse first.", { why: "The detergent works on the food left on the dishes.", cite: dishes("D1", "D2", "D5") }),
      s("stop", "Keep cast iron and wood out of the dishwasher.", { why: "Cast iron rusts and wood warps or cracks.", cite: dishes("D39", "CI4") }),
      s("box", "Put plates and pots on the bottom rack, facing the middle.", { why: "Water sprays up from the middle to reach them.", cite: dishes("D7", "D9") }),
      s("box", "Put cups, glasses and plastics on the top rack, face down.", { why: "Face down, water can't pool in them.", cite: dishes("D13", "D17") }),
      s("box", "Put knives in the basket point down.", { why: "Point down, they can't cut you when you unload.", cite: dishes("D20", "D22") }),
      s("check", "Leave space so water reaches every dish.", { why: "Crowded dishes come out dirty.", cite: dishes("D26", "D9") }),
      s("check", "Turn the spray arm by hand. Move dishes if it hits.", { check: { ask: "Does the spray arm turn freely?", ifNo: "Move the dish that stops it." }, why: "A blocked arm can't spray the dishes.", cite: dishes("D25") }),
      s("soap", "Fill the detergent cup with dishwasher detergent, never dish soap.", { why: "Dish soap makes suds that can push water out.", cite: dishes("D33", "D34") }),
      s("water", "Run the sink's hot water until hot, then turn it off.", { why: "So the dishwasher fills with hot water from the start.", cite: dishes("D27", "D35") }),
      s("power", "Start the dishwasher with heated dry.", { why: "Heated dry gives the best drying.", cite: dishes("D36") }),
    ] },
    // 5-minute version: the first essential Easy steps, nothing new (guide-kit quick).
    quick: { steps: [0, 1, 2, 3, 4, 7, 9] },
    citations: [...dishes("D1", "D2", "D3", "D4", "D5", "D6", "D7", "D8", "D9", "D10", "D11", "D12", "D13", "D14", "D15", "D16", "D17", "D18", "D19", "D20", "D21", "D22", "D23", "D24", "D25", "D26", "D27", "D28", "D29", "D31", "D33", "D34", "D35", "D36", "D38", "D39", "D40", "D41", "D42", "D43", "D46", "CI4"), ...food("H22"), ...clean("K7"), ...storage("H31", "H32")],
    keywords: ["load dishwasher", "loading", "load", "top rack", "bottom rack", "upper rack", "lower rack", "silverware", "cutlery", "knives", "plates", "glasses", "cups", "pre-rinse", "prerinse", "dishwasher safe", "rinse aid", "spray arm", "dishes in dishwasher", "what goes where", "cutting board in the dishwasher"],
    askMatch: /^(?!.*\b(oven|fridge|refrigerator|freezer|closet|washer|washing machine|dryer)\b).*(\b(dish|dishes|dishwasher|silverware|cutlery|rinse aid|spray arm)\b|\b(top|bottom|upper|lower|third) rack\b)/,
  }),
  guide({
    id: "cast-iron-wash", title: "Wash and dry cast iron", kind: "topic",
    when: ["After each use: wash, dry, then oil (Lodge)."],
    need: [
      "Warm, soapy water: dish soap won't strip the seasoning (Lodge).",
      { say: "A stiff-bristled brush or scrubbing pad (Lodge).", buy: ["scrub_brush", "scrub_pads"] },
      { say: "A nylon scrubbing brush or a pan scraper, for stuck-on food (Lodge).", buy: "scrub_brush" },
      "Paper towels or a lint-free cloth (Lodge).",
      { say: "Cooking oil (Lodge).", noBuy: "a pantry staple you already cook with" },
    ],
    steps: [
      "Wash the pan in warm, soapy water; today's dish soap won't remove the seasoning (Lodge).",
      "Scrub with a stiff-bristled brush or scrubbing pad (Lodge).",
      "Stuck-on food: work it off with a nylon scrubbing brush or a pan scraper, and rinse under warm water. Don't soak it (Lodge).",
      "Dry it completely right away with a paper towel or lint-free cloth. Air-drying can make it rust (Lodge).",
      "Spread a very thin, even layer of oil over the whole surface with a paper towel. It shouldn't drip or run when you tilt the pan (Lodge).",
      "Cook with it often: that's the best way to keep up the seasoning (Lodge).",
      "Rusty, sticky or flaking? See \"Fix rust and reseason cast iron\" (Lodge).",
    ],
    where: ["Put it away only when it is completely dry, out of damp places: moisture-prone places can make it rust (Lodge)."],
    never: [
      "Never soak cast iron: it rusts (Lodge).",
      "Never put cast iron in the dishwasher: it strips the seasoning and will likely rust (Lodge, Whirlpool).",
      "Never leave it to air-dry (Lodge).",
      "Don't use steel wool or a metal scrubber for everyday cleaning; save them for rust (Lodge).",
    ],
    easy: { title: "Wash cast iron", why: "Washing, drying and oiling keeps rust away.", steps: [
      s("water", "Wash the pan in warm, soapy water."),
      s("clean", "Scrub it with a stiff brush or scrub pad."),
      s("clean", "Scrape stuck food off with a pan scraper."),
      s("stop", "Never soak it or put it in the dishwasher."),
      s("towel", "Dry it all the way with a paper towel."),
      s("clean", "Spread a thin layer of oil with a paper towel."),
      s("towel", "Wipe off any oil that drips or runs."),
      s("box", "Store it only when it is fully dry."),
    ] },
    citations: dishes("CI1", "CI2", "CI3", "CI4", "CI5", "CI6", "CI9", "CI12", "CI13", "CI14", "D39"),
    products: [{ job: "Cast iron care tools (Lodge's own)", examples: ["Lodge Pan Scrapers", "Lodge Chainmail Scrubbing Pad", "Lodge 10 Inch Scrub Brush"], citations: dishes("CI13"), note: "Lodge's steps name a stiff-bristled brush or scrubbing pad, and a nylon brush or pan scraper for stuck-on food. Lodge sells the chainmail pad but its steps don't call for it." }],
    keywords: ["cast iron", "cast iron pan", "cast iron skillet", "skillet", "wash cast iron", "clean cast iron", "dry cast iron", "oil cast iron"],
    askMatch: /\bcast[- ]iron\b|\bskillets?\b/,
  }),
  guide({
    id: "cast-iron-rust", title: "Fix rust and reseason cast iron", kind: "topic",
    when: ["When you see rust (Lodge).", "When the cooking surface is sticky or gummy, or the seasoning flakes off (Lodge)."],
    need: [
      { say: "Steel wool or a metal scouring pad, or a Lodge Rust Eraser (Lodge).", buy: "steel_wool" },
      "Warm, soapy water.",
      { say: "Paper towels or a lint-free cloth (Lodge).", buy: "paper_towels" },
      { say: "An oil with a high smoke point: Lodge's test kitchen uses vegetable oil, melted shortening or canola oil (Lodge).", noBuy: "a pantry staple you already cook with" },
      { say: "Aluminum foil and an oven (Lodge).", noBuy: "aluminum foil from your kitchen drawer, and your own oven" },
    ],
    steps: [
      "Scour off the rust: with a Rust Eraser on a cool, dry pan, or with steel wool in warm, soapy water (Lodge).",
      "Rinse and dry it completely (Lodge).",
      "Rub on a very thin, even layer of cooking oil, inside and out (Lodge).",
      "A little rust may need nothing more. For a lot of rust, or when much of the seasoning is gone, reseason (Lodge).",
      "Reseason: preheat the oven to 450–500°F and put aluminum foil on the bottom rack to catch drips (Lodge).",
      "Oil it thinly inside and out; too much oil makes it sticky (Lodge).",
      "Bake it upside down on a middle or top rack (Lodge's pages give both) for 1 hour, then let it cool in the oven (Lodge).",
      "Sticky or gummy surface: that's built-up oil. Bake it upside down for 1 hour at 450–500°F (Lodge).",
      "Black flakes are carbonized oil and not harmful. Keep cooking, or wash, dry, oil and bake it for 1 hour at 450–500°F (Lodge).",
    ],
    where: ["Put it away only when it is completely dry, out of damp places (Lodge)."],
    never: [
      "Never use more than a thin coat of oil: it turns sticky (Lodge).",
      "Never soak it or put it in the dishwasher: both can cause rust (Lodge).",
    ],
    easy: { title: "Fix rust on cast iron", why: "Rust comes off, and new seasoning protects the pan.", steps: [
      s("clean", "Scrub the rust off with steel wool and soapy water."),
      s("water", "Rinse the pan."),
      s("towel", "Dry it all the way."),
      s("clean", "Spread a thin layer of oil inside and out."),
      s("thermo", "Heat the oven to 450 to 500 degrees."),
      s("box", "Put foil on the bottom rack."),
      s("box", "Put the pan upside down on a higher rack."),
      s("wait", "Leave it in the oven for one hour.", { waitSeconds: 3600 }),
      s("wait", "Let it cool in the oven."),
    ] },
    citations: dishes("CI3", "CI4", "CI5", "CI6", "CI7", "CI8", "CI9", "CI10", "CI11", "CI13"),
    products: [{ job: "Rust and reseasoning (Lodge's own)", examples: ["Lodge Rust Eraser", "Lodge Seasoning Spray"], citations: dishes("CI7", "CI13") }],
    keywords: ["rust", "rusty", "rusted", "cast iron rust", "skillet", "season", "seasoning", "reseason", "re-season", "sticky", "flaking", "black flakes"],
    askMatch: /\bcast[- ]iron\b|\bskillets?\b/,
  }),
  guide({
    id: "drying-mat", title: "Clean the dish drying mat", kind: "topic",
    suggested: "No maker or health source gives a cleaning interval for dish drying mats. CDC's \"every few days\" is for drying racks used for baby-feeding items; it is shown as the nearest authority, not as a mat rule.",
    when: [
      "Every few days is the nearest authority: CDC's rule for drying racks used for baby-feeding items (at least daily if the baby is under 2 months old, was born early, or has a weakened immune system). No source gives a mat interval.",
      "Sooner if it stays damp: CDC says drying racks can trap moisture and let mold and germs grow.",
    ],
    need: [
      { say: "Soap and warm water, or a dishwasher with hot water and heated dry if the mat's label says dishwasher-safe (CDC, for basins and brushes).", buy: "dish_soap" },
      { say: "For a microfiber mat: the washing machine (Umbra UDry).", noBuy: "your own washing machine" },
    ],
    steps: [
      "Let clean dishes air-dry on the mat; don't rub or pat them dry with a dish towel (CDC).",
      "Every few days, wash the mat: in the dishwasher with hot water and heated dry if its label says dishwasher-safe, or by hand with soap and warm water (CDC, for racks, basins and brushes).",
      "Microfiber mat: put it in the washing machine (Umbra UDry).",
      "Rubber or silicone mat: follow its care label. No mat maker's care text was found, so CDC's rack rule above is the nearest authority (CDC).",
      "Let it dry thoroughly before you use it again or fold it away: that helps keep mold and germs from growing (CDC).",
      "For baby-feeding items, CDC says drying on a clean dish towel or paper towel is probably more hygienic than a drying rack (CDC).",
    ],
    where: ["Fold or store the mat only when it is fully dry (CDC)."],
    never: ["Never put the mat away damp: damp racks can grow mold and germs (CDC)."],
    easy: { title: "Clean the drying mat", why: "A damp mat can grow mold and germs.", steps: [
      s("clean", "Let clean dishes air-dry on the mat."),
      s("stop", "Don't rub dishes dry with a towel."),
      s("soap", "Wash the mat every few days."),
      s("soap", "Use soap and warm water, or the dishwasher if allowed."),
      s("wait", "Let the mat dry all the way."),
      s("box", "Store it only when it is fully dry."),
    ] },
    citations: dishes("M1", "M2", "M3", "M4", "D38"),
    products: [{ job: "Machine-washable mat (maker example)", examples: ["Umbra UDry"], citations: dishes("M4") }],
    keywords: ["drying mat", "dish mat", "dish drying mat", "silicone mat", "rubber mat", "dish rack", "drying rack", "dish drainer", "mat"],
    askMatch: /\bdish(es)?\b.*\b(mats?|racks?|drainers?)\b|\bdrying (mats?|racks?)\b|\b(silicone|rubber) (dish |drying )?mats?\b|\bdish drainers?\b/,
  }),
  guide({
    id: "toaster-tray", title: "Empty the toaster crumb tray",
    when: ["After each use (per KitchenAid). The score checks weekly."],
    need: [{ say: "A soft, damp cotton cloth.", buy: "wiping_cloths" }],
    steps: [
      "Unplug the toaster and let it cool.",
      "Slide out the crumb tray and empty it; hand-wash only.",
      "Wipe the outside with a soft, damp cotton cloth.",
      "Put the tray back before the next use.",
    ],
    where: ["The tray stays in the toaster."],
    never: ["Never submerge the toaster.", "Never use paper towels, abrasives or liquid cleaners on it (KitchenAid).", "Never run it without the crumb tray."],
    easy: { title: "Empty the crumb tray", why: "Crumbs build up and can burn.", steps: [
      s("power", "Unplug the toaster."),
      s("wait", "Let it cool."),
      s("trash", "Pull out the crumb tray and empty it."),
      s("towel", "Wipe the outside with a damp soft cloth."),
      s("check", "Put the tray back in."),
      s("stop", "Never put the toaster in water."),
    ] },
    citations: clean("A13"), keywords: ["toaster", "crumb tray", "crumbs"],
  }),
  guide({
    id: "blender", title: "Clean the blender",
    when: ["Right after each use (per Vitamix). The score checks weekly."],
    need: ["Warm water and a few drops of dish soap.", { say: "A long-handled brush.", buy: "scrub_brush" }],
    steps: [
      "Rinse the container right after use.",
      "Fill it partway with warm water and a few drops of dish soap; blend one minute (or run the self-clean program).",
      "Rinse; use a long-handled brush near the blades.",
      "Wipe the motor base with a damp cloth.",
    ],
    where: ["Store the container dry, next to the base."],
    never: ["Never submerge the motor base or put it in the dishwasher (Vitamix).", "Never use abrasives."],
    easy: { title: "Clean the blender", why: "Food dries on the blades fast.", steps: [
      s("water", "Rinse the blender jar right after use."),
      s("soap", "Add warm water and a few drops of soap."),
      s("power", "Run the blender for one minute.", { waitSeconds: 60 }),
      s("water", "Rinse it out."),
      s("clean", "Use a long brush near the blades."),
      s("towel", "Wipe the base with a damp cloth."),
      s("stop", "Never put the base in water."),
    ] },
    citations: clean("A16", "A17"), keywords: ["blender", "vitamix"],
  }),
  guide({
    id: "air-fryer", title: "Wash the air fryer basket",
    when: ["After use. Ninja gives a method, not an interval; weekly is our suggested starting point."],
    need: ["Warm soapy water.", "A soft sponge."],
    steps: [
      "Let it cool and unplug it.",
      "Soak the basket and crisper plate in warm soapy water.",
      "Clean with a soft sponge; hand-washing extends their life (Ninja), though they're dishwasher-safe.",
      "Dry them fully before putting them back.",
    ],
    where: ["Basket back in the air fryer once dry."],
    never: ["Never use oven or grill cleaner: harsh cleaners ruin the nonstick (Ninja)."],
    easy: { title: "Wash the air fryer basket", why: "Old grease smokes and sticks.", steps: [
      s("power", "Unplug it and let it cool."),
      s("soap", "Soak the basket in warm soapy water."),
      s("clean", "Wipe it with a soft sponge."),
      s("towel", "Dry it all the way."),
      s("stop", "Never use oven cleaner on it."),
    ] },
    citations: clean("A19"), suggested: "Ninja gives a method, not an interval.", keywords: ["air fryer", "ninja", "basket"],
  }),
  guide({
    id: "stovetop-induction", title: "Clean the induction cooktop",
    when: ["Once it's cool. Weiman gives no interval; GE's glass-cooktop routine is daily.", "Sugary spills: right away (GE)."],
    need: ["A glass cooktop cleaner that says it's for induction (Weiman names induction).", "Paper towels or the cleaner's scrubbing pads.", { say: "A new, sharp single-edge razor scraper for stuck spots.", buy: "cooktop_scraper" }],
    steps: [
      "Let the surface cool (Weiman).",
      "Shake the cleaner and apply a little to the cooktop.",
      "Clean with the scrubbing pad or a paper towel.",
      "Buff with a dry paper towel.",
      "Sugar, jelly, syrup or melted plastic: these can pit glass. Push them off to a cool area with a new sharp razor scraper right away (GE).",
      "Follow your cooktop's own manual if it names a cleaner.",
    ],
    where: [WHERE.cleaners],
    never: [GLASS_NEVER, "Never use a dull or nicked blade (GE)."],
    easy: { title: "Clean the induction top", why: "Stuck food and sugar can mark the glass.", steps: [
      s("wait", "Let the cooktop cool.", { why: "The maker says to clean it cool.", cite: clean("S8") }),
      s("spray", "Put a little glass cooktop cleaner on it.", { why: "The maker names it for glass, ceramic and induction tops.", cite: clean("S8") }),
      s("clean", "Wipe it with a pad or paper towel.", { why: "The maker says to clean with its pads or a paper towel.", cite: clean("S8") }),
      s("towel", "Wipe it dry and shiny.", { why: "The maker says to buff it dry.", cite: clean("S8") }),
      s("stop", "Never use steel wool, bleach or ammonia on it.", { why: "They can scratch or harm the glass.", cite: clean("S6") }),
      s("clean", "Use a new sharp scraper on stuck spots, not steel wool.", { why: "Sugar can pit the glass. A dull blade can scratch it.", cite: clean("S1", "S3") }),
    ] },
    citations: clean("S1", "S2", "S3", "S6", "S8"),
    products: [{ job: "Glass, ceramic and induction cooktops", examples: ["Weiman Glass Cook Top Cleaner & Polish"], citations: clean("S8") }],
    suggested: "No induction maker's interval was found.", keywords: ["induction", "cooktop", "glass top", "stove", "stovetop"],
  }),
  guide({
    id: "stovetop-glass", title: "Clean the glass-ceramic cooktop",
    when: ["Daily cleaning (per GE).", "Sugary spills: right away, even while hot (GE, Samsung)."],
    need: [{ say: "A ceramic cooktop cleaning cream (GE: always recommended).", buy: "cooktop_cleaner" }, { say: "A paper towel or cleaning pad.", buy: ["paper_towels", "scrub_pads"] }, { say: "A new, sharp single-edge razor scraper.", buy: "cooktop_scraper" }],
    steps: [
      "Turn the controls off. For daily cleaning, put a few drops of cooktop cream on the cooktop (GE).",
      "Clean the whole surface with a paper towel or cleaning pad.",
      "Remove residue with a dry cloth.",
      "Stuck residue: scrape while warm, not hot, with the scraper at about 45° (Whirlpool).",
      "Sugary spills or melted plastic: turn off the element and push them to a cool area with a new sharp scraper (GE).",
      "Follow your cooktop's manual: GE says only its recommended cleaners; Samsung also allows baking soda and a vinegar spray.",
    ],
    where: [WHERE.cleaners],
    never: [GLASS_NEVER, "Never use a dull or nicked blade (GE).", "Never use sponges or cleaners other than those the maker recommends: they can damage the cooktop (GE)."],
    easy: { title: "Clean the glass cooktop", why: "Sugar and burned food can pit the glass.", steps: [
      s("power", "Turn the burners off.", { why: "Whirlpool says to turn the controls off first.", cite: clean("S5") }),
      s("spray", "Put a few drops of cooktop cream on it.", { why: "GE always recommends a ceramic cooktop cream.", cite: clean("S2") }),
      s("clean", "Wipe the whole top with a pad.", { why: "GE says to clean the whole top with a pad or paper towel.", cite: clean("S2") }),
      s("towel", "Wipe it dry with a cloth.", { why: "GE says to remove the leftover cream with a dry cloth.", cite: clean("S2") }),
      s("clean", "Scrape stuck spots with a new sharp blade, not steel wool.", { why: "Sugar and melted plastic can pit the glass.", cite: clean("S1", "S3", "S5") }),
      s("stop", "Never use steel wool, bleach or ammonia on it.", { why: "Whirlpool names these as things that harm the top.", cite: clean("S6") }),
    ] },
    citations: clean("S1", "S2", "S3", "S4", "S5", "S6", "S7"),
    products: [
      { job: "Glass-ceramic cooktop", examples: ["CeramaBryte Ceramic Cooktop Cleaner and pads (GE)", "affresh Cooktop Cleaner (Whirlpool)", "Weiman Glass Cook Top Cleaner & Polish"], citations: clean("S1", "S5", "S8") },
    ],
    keywords: ["glass cooktop", "ceramic cooktop", "glass top", "radiant", "stove", "stovetop", "cooktop"],
  }),
  guide({
    id: "stovetop-gas", title: "Clean the gas grates and burner caps",
    when: ["GE and Whirlpool give methods, not intervals; weekly is our suggested starting point.", "Acidic spills: once the parts are cool (Whirlpool)."],
    need: ["Hot soapy water and a sink or tub.", { say: "A nonabrasive scrubber and a mild abrasive cleaner (Whirlpool).", buy: ["scrub_pads", "grate_cleaner"] }, { say: "A paper clip or twist-tie wire for burner grooves (GE).", noBuy: "a paper clip or twist-tie from a drawer" }],
    steps: [
      "Let everything cool.",
      "Lift off the grates and caps. Soak them in hot soapy water at least 20 minutes (Whirlpool).",
      "Scrub with a nonabrasive scrubber; for heavy soil, a paste of 3 parts baking soda to 1 part water.",
      "Rinse and dry fully before putting them back (GE).",
      "Grates in the dishwasher only if your manual says so (GE: only enamel-coated underneath, rubber feet off).",
      "Heavy soil (GE): seal grates and caps in a bag with 1/4 cup of ammonia for 3 hours to overnight. " + AMMONIA_WARN,
      "Brass caps (GE): soak in vinegar and water.",
      "Clear clogged burner grooves with a paper clip or twist-tie wire.",
    ],
    where: [WHERE.cleaners],
    never: ["Never put burner caps in the dishwasher (Whirlpool).", "Never use steel wool or metal scouring pads on burner parts: they clog the ports (GE).", NEVER.mixBleach],
    easy: { title: "Clean the gas grates", why: "Grease on burners burns and clogs the flame.", steps: [
      s("wait", "Let the stove cool."),
      s("box", "Lift off the grates and caps."),
      s("soap", "Soak them in hot soapy water.", { waitSeconds: 1200 }),
      s("clean", "Scrub them with a soft scrubber."),
      s("water", "Rinse them."),
      s("towel", "Dry them all the way before you put them back."),
      s("stop", "Never put burner caps in the dishwasher."),
      s("stop", "Never use ammonia and bleach together."),
    ] },
    citations: [...clean("S9", "S10", "S11", "S12", "G10"), ...NEVER_CITES.mixBleach],
    products: [{ job: "Brass burner caps, to restore shine", examples: ["Brasso", "Bar Keepers Friend"], citations: clean("S9") }],
    suggested: "GE and Whirlpool give methods, not intervals.", keywords: ["gas stove", "gas range", "grates", "burner caps", "stove", "stovetop"],
  }),
  guide({
    id: "stovetop-coil", title: "Clean the coil elements and drip bowls",
    when: ["GE and Whirlpool give methods, not intervals; monthly is our suggested starting point."],
    need: [{ say: "Liquid detergent or an all-purpose cleaner.", buy: "all_purpose_cleaner" }, { say: "For chrome drip bowls: ammonia and a gallon of water (Whirlpool).", noBuy: "ammonia is never mixed with bleach or other cleaners, so we leave the choice and the brand to you; read its label" }, { say: "A stainless steel wool pad, only if needed on chrome bowls.", buy: "steel_wool" }],
    steps: [
      "Burn off soil on the element: set it to HIGH for about 1 minute (GE).",
      "Once cool, wipe off excess grease.",
      "Unplug the elements and lift out the drip bowls.",
      "Wash drip bowls with liquid detergent or all-purpose cleaner, rinse, dry (Whirlpool).",
      "Chrome bowls, heavy soil: soak 20 minutes in 1/2 cup ammonia per gallon of water (Whirlpool). " + AMMONIA_WARN,
      "If still needed on chrome bowls: a stainless steel wool pad or mild abrasive.",
    ],
    where: [WHERE.cleaners],
    never: ["Never put a coil element in water (GE).", "Never clean plug-in elements in the self-clean oven or the dishwasher (GE).", NEVER.mixBleach],
    easy: { title: "Clean the coil stove", why: "Burned grease smokes and smells.", steps: [
      s("power", "Turn the coil to high for 1 minute.", { waitSeconds: 60 }),
      s("wait", "Turn it off and let it cool."),
      s("towel", "Wipe off grease."),
      s("box", "Lift out the drip bowls."),
      s("soap", "Wash the bowls with soap, then rinse and dry."),
      s("stop", "Never put the coil in water."),
      s("stop", "Never use ammonia and bleach together."),
    ] },
    citations: [...clean("S13", "S14", "G10"), ...NEVER_CITES.mixBleach],
    suggested: "GE and Whirlpool give methods, not intervals.", keywords: ["coil stove", "electric coil", "drip bowls", "stove", "stovetop", "burner"],
  }),
  guide({
    id: "espresso-daily", title: "Rinse the espresso machine",
    when: ["Each day you use it (per Breville)."],
    need: [{ say: "A clean towel just for the steam wand.", buy: "dish_towels" }, { say: "Hot water.", noBuy: "tap water" }],
    steps: [
      "Knock out the puck, then rinse and wipe the portafilter (Breville).",
      "Run a blank shot through the group head.",
      "Purge the steam wand into a towel and wipe it after every milk use.",
      "Rinse baskets and portafilter under hot water right after use.",
      "Hand-wash parts with mild dish liquid; no abrasives, no dishwasher (Breville BES870).",
      "Check your own machine's manual; these steps are Breville's.",
    ],
    where: ["Parts dry on a rack next to the machine."],
    never: ["Never put Breville parts in the dishwasher or use abrasives (BES870 manual)."],
    easy: { title: "Rinse the espresso machine", why: "Old coffee oil and milk turn sour.", steps: [
      s("trash", "Dump the used coffee in the trash."),
      s("water", "Rinse and wipe the portafilter."),
      s("water", "Run water through with no coffee."),
      s("towel", "Let a little steam out into a towel."),
      s("towel", "Wipe the steam wand."),
      s("stop", "Never put the parts in the dishwasher."),
    ] },
    citations: clean("A2", "A3"), keywords: ["espresso", "espresso machine", "portafilter", "steam wand", "breville"],
  }),
  guide({
    id: "espresso-filter", title: "Replace the espresso water filter",
    when: ["Every two months, more often with hard water (per Breville BES870). Other makers: check your manual."],
    need: [{ say: "The replacement filter your machine's maker names.", buy: "water_filter_cartridge" }],
    steps: [
      "Take out the old filter from the water tank.",
      "Soak and fit the new one as your manual says.",
      "Write down the date.",
      "Replacing the filter on time cuts the need to descale (Breville).",
    ],
    where: [WHERE.filters],
    never: ["Never use a filter your machine's maker doesn't list."],
    easy: { title: "Change the water filter", why: "A fresh filter means less scale in the machine.", steps: [
      s("box", "Take the old filter out of the tank."),
      s("check", "Read your machine's manual for the new one."),
      s("box", "Put in the new filter."),
      s("write", "Write down today's date."),
    ] },
    citations: clean("A2"), products: [{ job: "Breville espresso water filter", examples: ["Breville water filter"], citations: clean("A2") }],
    keywords: ["espresso filter", "water filter", "breville", "espresso"],
  }),
  guide({
    id: "espresso-descale", title: "Descale the espresso machine",
    when: ["When your machine prompts: Breville when the CLEAN/DESCALE light is solid; De'Longhi when prompted and at least every 200 uses.", "A flashing Breville light means a cleaning cycle (backflush), not descaling."],
    need: ["The descaler your machine's maker names.", { say: "Check your machine's manual. Breville allows vinegar, De'Longhi says no. Model unknown? No vinegar.", noBuy: "your machine's manual decides; the descaler itself is a step above" }],
    steps: [
      "Follow your machine's own descaling program.",
      "Breville BES870: dissolve one Breville descaling sachet in 1 L (34 fl oz) of water.",
      "De'Longhi: use EcoDecalk, 100 ml per descale; the program takes about 30–50 minutes.",
      "Cleaning cycle (Breville flashing light): backflush with the cleaning disc and 1 cleaning tablet in the 1-cup basket, about 5 minutes.",
      "Or Urnex Cafiza powder backflush: up to 1/2 tsp (3 g) in a blind filter; brew 10 s, stop 10 s, 5 times; rinse 5 times with no powder; pull and throw away one espresso.",
      "Run fresh water through to rinse.",
    ],
    where: [WHERE.filters],
    never: ["Never use vinegar unless your machine's manual allows it: De'Longhi says it may harm internal parts, and Urnex says it's hard to rinse.", "Never drink the first espresso after a backflush (Urnex)."],
    easy: { title: "Descale the espresso machine", why: "Scale blocks water and hurts the machine.", steps: [
      s("check", "Read your machine's manual first."),
      s("stop", "Don't use vinegar unless the manual says you can."),
      s("chemical", "Mix the maker's descaler with water."),
      s("water", "Pour it into the water tank."),
      s("power", "Start the descale program."),
      s("water", "Run clean water through after."),
    ] },
    citations: clean("A1", "A4", "A6", "A7", "A8", "A9"),
    products: [
      { job: "Breville espresso", examples: ["Breville descaling powder", "Breville cleaning tablets"], citations: clean("A1") },
      { job: "De'Longhi espresso", examples: ["De'Longhi EcoDecalk"], citations: clean("A8", "A9") },
      { job: "Espresso backflush and parts soak", examples: ["Urnex Cafiza powder"], citations: clean("A4") },
    ],
    suggested: "Makers set descaling by prompt or uses, not days; the reminder date is ours.", keywords: ["descale", "espresso", "espresso machine", "delonghi", "breville", "vinegar", "backflush"],
  }),
  guide({
    id: "coffee-descale", title: "Descale the coffee maker",
    when: ["Every 3 months (per Urnex Dezcal).", "Mr. Coffee says monthly; Keurig says every 3–6 months or when the brewer prompts."],
    need: [{ say: "A descaler, or white vinegar only if your brand's instructions use it.", buy: ["descaler", "white_vinegar"] }],
    steps: [
      "Urnex Dezcal: dissolve 1 packet (28 g) in 32 oz (1 L) of lukewarm water, brew a full tank and pour it out, then brew 3 full tanks of fresh water.",
      "Cuisinart (per Cuisinart): 2 parts water to 1 part vinegar; Clean-button models 3:1, press and hold Clean; then one full cycle of plain water.",
      "Mr. Coffee (per Mr. Coffee): about 50% water and 50% white vinegar, then two water-only brews.",
      "Keurig (per Keurig): the whole bottle of Keurig Descaling Solution plus a bottle of water, no pod, then fresh-water rinses.",
      "Follow your own brewer's manual when it differs.",
    ],
    where: [WHERE.filters],
    never: ["Never mix vinegar with a descaler or any bleach product.", "Urnex notes vinegar is hard to rinse and can leave an odor and aftertaste."],
    easy: { title: "Descale the coffee maker", why: "Scale slows the brewer and changes the taste.", steps: [
      s("check", "Read your coffee maker's manual."),
      s("chemical", "Mix the descaler with water the way it says."),
      s("water", "Pour it into the water tank."),
      s("power", "Run a full brew and pour it out."),
      s("water", "Run clean water through three times."),
    ] },
    citations: clean("A6", "A10", "A11", "A12"),
    products: [
      { job: "Drip maker descaling", examples: ["Urnex Dezcal Descaling Powder (home)"], citations: clean("A6") },
      { job: "Keurig brewer", examples: ["Keurig Descaling Solution"], citations: clean("A12") },
    ],
    keywords: ["coffee maker", "descale", "drip coffee", "keurig", "vinegar", "mr coffee", "cuisinart"],
  }),
  guide({
    id: "kettle-descale", title: "Descale the kettle",
    when: ["No kettle maker's interval was found. Follow your kettle's manual; the 3-month reminder is ours."],
    need: [{ say: "What your kettle's manual names.", buy: "descaler" }],
    steps: [
      "Find your kettle's manual (paper or the maker's website).",
      "Follow its descaling steps and product.",
      "Rinse and boil fresh water once before use, if the manual says so.",
    ],
    where: [WHERE.filters],
    never: ["Never use a recipe from a blog as if it were the maker's instructions: we found no maker's method to give you."],
    easy: { title: "Descale the kettle", why: "Scale builds up in hard water.", steps: [
      s("check", "Read your kettle's manual."),
      s("check", "Use only what the manual says."),
      s("water", "Rinse the kettle well after."),
    ] },
    citations: [], suggested: "No kettle maker's interval or method was found (home-cleaning-appliances UNVERIFIED). Follow your kettle's manual.",
    keywords: ["kettle", "descale kettle", "electric kettle"],
  }),
  guide({
    id: "pitcher-filter", title: "Change the pitcher filter",
    when: ["Per Brita: Standard every 40 gallons (about 2 months); Elite every 120 gallons (about 6 months); Stream every 40 gallons (about 2 months); faucet filters every 100 gallons (about 4 months). Sooner with hard water.", "Other makers: follow the filter's box."],
    need: [{ say: "The replacement filter for your pitcher.", buy: "water_filter_cartridge" }],
    steps: [
      "Take out the old filter.",
      "Prepare and fit the new one as the box says.",
      "Write down the date, or set the pitcher's indicator.",
    ],
    where: [WHERE.filters],
    never: ["Never keep a filter past the maker's interval: Brita's times assume 11 glasses a day."],
    easy: { title: "Change the pitcher filter", why: "An old filter stops working.", steps: [
      s("box", "Take out the old filter."),
      s("check", "Read the new filter's box."),
      s("box", "Put in the new filter."),
      s("write", "Write down today's date."),
    ] },
    citations: clean("W1"), products: [{ job: "Pitcher filters", examples: ["Brita Standard/Original", "Brita Elite", "Brita Stream"], citations: clean("W1") }],
    keywords: ["brita", "pitcher filter", "water filter", "filter", "pitcher"],
  }),
  guide({
    id: "fridge-filter", title: "Change the fridge water filter",
    when: ["Every 6 months (per Whirlpool and GE), sooner if the flow drops. Indicators alert by gallons, which differ by model."],
    need: [{ say: "The filter your fridge's maker names.", buy: "water_filter_cartridge" }],
    steps: [
      "Take out the old filter as your manual shows.",
      "Put in the new one.",
      "Flush it: Whirlpool says flush after installing; GE says dispense about 1–2 gallons to purge air.",
      "Reset the filter light if your fridge has one.",
    ],
    where: [WHERE.filters],
    never: ["Never drink the first water before flushing a new filter (it can carry air and carbon fines, per Whirlpool)."],
    easy: { title: "Change the fridge filter", why: "An old filter stops cleaning the water.", steps: [
      s("check", "Read your fridge manual."),
      s("box", "Take out the old filter."),
      s("box", "Put in the new filter."),
      s("water", "Run 1 to 2 gallons through to flush it."),
      s("check", "Turn off the filter light the way the manual says."),
    ] },
    citations: clean("W2", "W3"), products: [{ job: "Fridge filters (Whirlpool)", examples: ["everydrop"], citations: clean("W2") }],
    keywords: ["fridge filter", "refrigerator filter", "water filter", "everydrop", "filter"],
  }),
  guide({
    id: "ro-filters", title: "Change the RO pre-filters",
    when: ["Per maker: APEC, at least every 12 months (sooner on well water); iSpring, every 6–12 months. Other systems: your manual."],
    need: [{ say: "The replacement filter set your RO maker names.", buy: "water_filter_cartridge" }],
    steps: [
      "Turn off the water to the system as your manual says.",
      "Change the pre-filters (APEC stages 1–3).",
      "APEC also says change the housing O-ring yearly.",
      "Turn the water back on and check for leaks.",
      "Follow your manual to flush the new filters.",
    ],
    where: [WHERE.filters],
    never: ["Never treat NSF/ANSI 58 as a schedule: it certifies systems, the schedule is in your maker's manual."],
    easy: { title: "Change the RO filters", why: "Old filters stop cleaning the water.", steps: [
      s("check", "Read your RO system's manual."),
      s("water", "Turn off the water to the system."),
      s("wrench", "Change the filters the way the manual shows."),
      s("water", "Turn the water back on."),
      s("check", "Look for leaks."),
    ] },
    citations: clean("W4", "W5", "W6", "W7"), keywords: ["ro", "reverse osmosis", "ro filter", "under sink filter", "filter"],
  }),
  guide({
    id: "ro-membrane", title: "Change the RO membrane",
    when: ["Per APEC RO-90: every 3–5 years on city water, 2–3 years on well water, or when product-water TDS reaches 15–20% of the input. Per iSpring: every 2–3 years."],
    need: [{ say: "The membrane your RO maker names.", noBuy: "model-specific: the part number comes from your RO maker's manual" }, { say: "A TDS meter, if you check by TDS.", noBuy: "optional; only if you check by TDS" }],
    steps: [
      "Turn off the water to the system.",
      "Change the membrane (APEC stage 4) as your manual shows.",
      "APEC post-carbon (stage 5): every 3–5 years.",
      "Turn the water back on, check for leaks and flush as your manual says.",
    ],
    where: [WHERE.filters],
    never: ["Never guess the interval from another brand: the membrane schedule belongs to your system's maker."],
    easy: { title: "Change the RO membrane", why: "An old membrane lets more through.", steps: [
      s("check", "Read your RO system's manual."),
      s("water", "Turn off the water to the system."),
      s("wrench", "Change the membrane the way the manual shows."),
      s("water", "Turn the water back on."),
      s("check", "Look for leaks."),
    ] },
    citations: clean("W5", "W6"), keywords: ["ro membrane", "reverse osmosis", "membrane", "ro"],
  }),

  /* ───────── topic guides ───────── */
  guide({
    id: "one-sink-raw-to-veg", kind: "topic", title: "One sink: raw meat, then vegetables",
    when: ["Any meal with raw meat, poultry or seafood and food you won't cook (salad, fruit, bread), in a kitchen with one sink."],
    need: [{ say: "Two cutting boards: one for produce, bread and ready-to-eat food, one for raw meat, poultry and seafood.", buy: "cutting_board" }, "Hot soapy water.", "For counters and boards (FSIS): unscented liquid chlorine bleach, 1 tablespoon per gallon of water.", { say: "A clean dish tub or basin (optional).", buy: "dish_tub" }, { say: "A produce brush.", buy: "produce_brush" }],
    steps: [
      "Make the food you won't cook first (vegetables, salads), before you handle raw meat and poultry (USDA).",
      "Rinse fresh produce under running water, even produce whose skin you don't eat. Scrub firm produce with a clean produce brush.",
      "Use one board for produce and ready-to-eat food and a separate one for raw meat, poultry and seafood.",
      "Don't wash or rinse raw poultry. In the USDA study, 60% of people who washed poultry had bacteria in the sink, 14% still did after cleaning it, and 26% got bacteria onto salad lettuce.",
      "After each food item, wash boards, dishes, utensils and counters with hot soapy water.",
      "After the raw meat: clean the sink and counters with hot soapy water, then apply the sanitizer. " + BLEACH_BOARDS,
      "Never put cooked or ready-to-eat food on a plate that held raw meat, poultry, seafood or eggs.",
      "A clean basin keeps items out of the sink: CDC says this for baby feeding items, because the sink may carry germs; using it for all dishes is our reading of CDC's reason.",
      "Wash your hands before, during and after preparing food: wet, soap, scrub at least 20 seconds, rinse, dry with a clean towel.",
    ],
    where: [WHERE.cleaners, WHERE.bleachMix, WHERE.food],
    never: ["Never wash raw poultry: even people who didn't wash it got bacteria onto lettuce 31% of the time, and washing made it worse.", NEVER.mixBleach, "Never use the vomit-cleanup bleach strength on food counters."],
    easy: { title: "Raw meat and vegetables", why: "One sink can spread raw meat germs to salad.", steps: [
      s("food", "Make the salad and vegetables first."),
      s("water", "Rinse produce under running water."),
      s("box", "Use a different board for raw meat."),
      s("stop", "Don't wash raw chicken."),
      s("soap", "Wash the board and knife with hot soapy water."),
      s("soap", "Wash the sink and counter with hot soapy water."),
      s("chemical", "Mix 1 tablespoon bleach in 1 gallon of water."),
      s("spray", "Wet the sink and counter with the bleach mix.", { waitSeconds: 180 }),
      s("hands", "Wash your hands for 20 seconds.", { waitSeconds: 20 }),
      s("stop", "Never put cooked food on the raw meat plate."),
    ] },
    citations: [...food("H18", "H19", "H20", "H21", "H23", "H24", "H25", "H26", "H27", "H41", "H42"), ...NEVER_CITES.mixBleach],
    keywords: ["one sink", "single sink", "raw meat", "vegetables", "salad", "chicken", "cutting board", "wash chicken", "cross contamination"],
  }),
  guide({
    id: "vomit-cleanup-home", kind: "topic", title: "Clean up vomit or diarrhea at home",
    when: ["Right away, whenever someone throws up or has diarrhea outside the toilet."],
    need: ["Rubber or disposable gloves.", "Paper towels and a plastic trash bag.", { say: "For vomit and diarrhea (CDC): household bleach (5%–8%), 5 to 25 tablespoons per gallon of water (1,000–5,000 ppm), or an EPA-registered disinfectant for norovirus.", buy: ["bleach", "norovirus_disinfectant"] }, { say: "Soap and hot water.", noBuy: "soap you already wash with: it cleans the spot, it does not disinfect it (see the never line)" }],
    steps: [
      "Put on rubber or disposable gloves.",
      "Wipe up the whole area with paper towels and put them in a plastic trash bag.",
      "Mix for vomit and diarrhea: 5 to 25 tablespoons of household bleach per gallon of water. About 1,000 ppm by bleach strength (UMN): 1/3 cup of 5.25% bleach, 1/4 cup of 6–6.25%, or 2½ tablespoons of 8.25% per gallon.",
      "Leave the bleach on at least 5 minutes, keeping the surface wet the whole time.",
      "Then clean the whole area again with soap and hot water.",
      "Counters and anything else that touches food: rinse with clean water last, then let it air-dry (bleach label).",
      "Disinfect nearby touch points too: toilet, floor, counters, doorknobs, switches, phones and remotes (Cleveland Clinic).",
      "Laundry: take soiled items off right away, handle with gloves without shaking, wash with detergent in hot water on the longest cycle, and machine dry on the highest heat.",
      "Wash your hands with soap and water; hand sanitizer doesn't work well against norovirus.",
    ],
    where: [WHERE.cleaners, WHERE.bleachMix],
    never: ["Never rely on alcohol, dish soap or disinfecting wipes: they won't kill norovirus (Cleveland Clinic), unless a product's label claims norovirus.", "Never use the counter mix (1 tablespoon per gallon) for vomit: it is far too weak for this job.", NEVER.mixBleach, "Never shake soiled laundry."],
    easy: { title: "Someone threw up", why: "Throw-up can spread a stomach bug to everyone.", steps: [
      s("people", "Keep people away from it."),
      s("gloves", "Put on gloves."),
      s("towel", "Wipe it up with paper towels."),
      s("trash", "Put the towels in a plastic bag."),
      s("chemical", "Mix 5 to 25 tablespoons bleach in 1 gallon of water."),
      s("spray", "Wet the spot and keep it wet.", { waitSeconds: 300 }),
      s("soap", "Clean the spot again with soap and hot water."),
      s("water", "Wash soiled clothes hot, then dry on high."),
      s("hands", "Wash your hands with soap and water."),
    ] },
    citations: [...food("H43", "H45", "H46", "H47"), ...hygiene("C4", "C5", "C6"), ...clean("B12", "K9"), ...NEVER_CITES.mixBleach],
    keywords: ["vomit", "threw up", "diarrhea", "norovirus", "stomach bug", "sick"],
  }),
  guide({
    id: "fridge-placement", kind: "topic", title: "Where food goes in the fridge",
    when: ["Every time you put groceries or leftovers away."],
    need: [{ say: "Sealed containers or sealable bags for raw meat, poultry and seafood.", buy: "food_containers" }, { say: "An appliance thermometer.", buy: "fridge_thermometer" }],
    steps: [
      "Keep the fridge at 40°F or below and the freezer at 0°F.",
      "Raw meat, poultry and seafood: sealed in containers or sealable bags, on the bottom shelf or bottom of the fridge.",
      "Why the bottom shelf: to stop drips onto other food. A fridge at 40°F or below is safe for any food anywhere inside; the shelf isn't colder (USDA FSIS).",
      "Eggs in their carton on a shelf; milk and other perishables on the main shelves.",
      "Vegetables in the higher-humidity crisper, fruit in the lower-humidity one; meat and cheese drawers are colder.",
      "Refrigerate all pre-cut or packaged produce.",
      "Don't overpack: cold air has to circulate.",
    ],
    where: [WHERE.food],
    never: ["Never store perishable foods in the door: door bins swing in temperature more than the main cabinet (FSIS, FDA).", "Never keep raw meat above food you won't cook, or unwrapped."],
    easy: { title: "Put food in the fridge", why: "Raw meat juice can drip onto other food.", steps: [
      s("box", "Put raw meat in a sealed container or bag."),
      s("fridge", "Put raw meat on the bottom shelf."),
      s("food", "Keep eggs in their carton on a shelf."),
      s("stop", "Don't keep milk or eggs in the door."),
      s("food", "Put vegetables and fruit in separate drawers."),
      s("fridge", "Leave space so cold air can move."),
      s("thermo", "Keep the fridge at 40°F or colder."),
    ] },
    citations: storage("H20", "H21", "H22", "H23", "H24", "H25"), keywords: ["fridge", "where to put", "bottom shelf", "raw meat", "door", "eggs", "milk", "crisper", "store food in fridge"],
  }),
];
